import type {
  AreaChange,
  WorkEnvironmentEvaluation,
  WorkEnvironmentSurvey,
} from '../../api/workEnvironmentTypes';
import { WorkEnvironmentSurveyStatus } from '../../api/workEnvironmentTypes';
import { archivedAfterEvaluation } from './archiveView';
import { dayAndMonth } from './surveyProcessView';

/*
 * Whether the evaluation can start, and how the areas compare, is decided in the API.
 * What is here only picks what the page shows.
 */

export const RESPONSE_DAYS = [7, 14, 21] as const;

/** The result stays on the page once the work is archived */
export const showsEvaluationPage = (
  survey: Pick<
    WorkEnvironmentSurvey,
    'status' | 'riskCalculatedAt' | 'actionPlanOpenedAt'
  >,
) =>
  Boolean(survey.riskCalculatedAt) &&
  (survey.status === WorkEnvironmentSurveyStatus.EVALUATING ||
    archivedAfterEvaluation(survey));

export type EvaluationPhase =
  | 'answering'
  | 'calculating'
  | 'insufficient'
  | 'result';

const PHASE_OF_STATUS: Partial<
  Record<WorkEnvironmentSurveyStatus, EvaluationPhase>
> = {
  [WorkEnvironmentSurveyStatus.AWAITING_RISK_CALC]: 'calculating',
  [WorkEnvironmentSurveyStatus.INSUFFICIENT_RESPONSES]: 'insufficient',
};

export const evaluationPhase = ({
  evaluation,
  result,
}: WorkEnvironmentEvaluation): EvaluationPhase =>
  result?.status === 'assessed'
    ? 'result'
    : (PHASE_OF_STATUS[evaluation.status] ?? 'answering');

type ComparedArea = NonNullable<
  WorkEnvironmentEvaluation['result']
>['areas'][number];

export type ChangeFilter = 'all' | AreaChange;

export const CHANGE_FILTERS: ChangeFilter[] = [
  'all',
  'worse',
  'better',
  'same',
];

export const filterAreas = (areas: ComparedArea[], filter: ChangeFilter) =>
  filter === 'all' ? areas : areas.filter((area) => area.change === filter);

const countOf = (areas: ComparedArea[], change: AreaChange) =>
  areas.filter((area) => area.change === change).length;

export const changeCounts = (areas: ComparedArea[]) => ({
  better: countOf(areas, 'better'),
  same: countOf(areas, 'same'),
  worse: countOf(areas, 'worse'),
  stillHigh: areas.filter((area) => area.after?.level === 'high').length,
});

/** The text that explains why the change and the risk level differ, if they do */
export const readingKey = (area: Pick<ComparedArea, 'change' | 'reading'>) =>
  area.reading === 'meanOnly' && area.change !== 'same' && area.change
    ? `workEnvironmentEvaluation.result.reading.meanOnly.${area.change}`
    : area.reading && area.reading !== 'meanOnly'
      ? `workEnvironmentEvaluation.result.reading.${area.reading}`
      : null;

/** The areas of the plans, with how they did */
export const focusResults = (data: WorkEnvironmentEvaluation) =>
  data.focus.map((focus) => ({
    ...focus,
    result: data.result?.areas.find((area) => area.key === focus.key) ?? null,
  }));

/** «september 2026» */
export const monthAndYear = (date: string, language: string) =>
  new Intl.DateTimeFormat(language === 'en' ? 'en-GB' : 'nb-NO', {
    month: 'long',
    year: 'numeric',
  }).format(new Date(date));

const sameMonth = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

/** Two measurements in the same month are told apart by the day */
export const comparedPeriods = (
  before: string,
  after: string,
  language: string,
) =>
  sameMonth(new Date(before), new Date(after))
    ? {
        before: dayAndMonth(before, language),
        after: dayAndMonth(after, language),
      }
    : {
        before: monthAndYear(before, language),
        after: monthAndYear(after, language),
      };

/** Once the result is in, what is left of the step is to archive the work */
export const evaluationStep = (phase: EvaluationPhase, archived: boolean) => ({
  finished: archived,
  awaitingArchive: phase === 'result' && !archived,
});
